import React from 'react';
import { MessageCircle, Send, Mail, AlertCircle, TrendingUp, Users } from 'lucide-react';

const CommunicationStats = ({ stats }) => {
  const statCards = [
    {
      title: 'Total Messages',
      value: stats.total,
      icon: MessageCircle,
      color: 'blue',
      description: `${stats.period_days} days`
    },
    {
      title: 'Sent Messages',
      value: stats.sent,
      icon: Send,
      color: 'green',
      description: 'Outbound'
    },
    {
      title: 'Received Messages',
      value: stats.received,
      icon: Mail,
      color: 'purple',
      description: 'Inbound'
    },
    {
      title: 'Unread Messages',
      value: stats.unread,
      icon: AlertCircle,
      color: 'red',
      description: 'Needs attention'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-gray-50',
        icon: 'text-yellow-600',
        text: 'text-gray-900'
      },
      green: {
        bg: 'bg-green-50',
        icon: 'text-green-600',
        text: 'text-green-900'
      },
      purple: {
        bg: 'bg-purple-50',
        icon: 'text-purple-600',
        text: 'text-purple-900'
      },
      red: {
        bg: 'bg-red-50',
        icon: 'text-red-600',
        text: 'text-red-900'
      }
    };
    return colors[color];
  };

  const responseRate = stats.sent > 0 ? ((stats.received / stats.sent) * 100).toFixed(1) : 0;
  const smsPercentage = stats.total > 0 ? ((stats.by_type.sms / stats.total) * 100).toFixed(1) : 0;
  const emailPercentage = stats.total > 0 ? ((stats.by_type.email / stats.total) * 100).toFixed(1) : 0;

  return (
    <div className="mb-6">
      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {statCards.map((stat) => {
          const colors = getColorClasses(stat.color);
          const Icon = stat.icon;
          
          return (
            <div key={stat.title} className="bg-white rounded-lg shadow-sm p-6 border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {stat.title}
                  </p>
                  <p className={`text-2xl font-bold ${colors.text}`}>
                    {stat.value.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stat.description}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${colors.bg}`}>
                  <Icon className={`w-6 h-6 ${colors.icon}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Communication Breakdown */}
        <div className="bg-white rounded-lg shadow-sm p-4 border">
          <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Message Types
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm text-gray-600">SMS</span>
              </div>
              <span className="text-sm font-medium">
                {stats.by_type.sms} ({smsPercentage}%)
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-yellow-600" />
                <span className="text-sm text-gray-600">Email</span>
              </div>
              <span className="text-sm font-medium">
                {stats.by_type.email} ({emailPercentage}%)
              </span>
            </div>
          </div>
        </div>

        {/* Response Rate */}
        <div className="bg-white rounded-lg shadow-sm p-4 border">
          <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Response Rate
          </h3>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600 mb-1">
              {responseRate}%
            </div>
            <p className="text-xs text-gray-500">
              Client responses per message sent
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm p-4 border">
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Quick Actions
          </h3>
          <div className="space-y-2">
            {stats.unread > 0 && (
              <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                <span className="text-sm text-red-700">Unread messages</span>
                <span className="text-sm font-medium text-red-900">
                  {stats.unread}
                </span>
              </div>
            )}
            
            <div className="text-xs text-gray-500 pt-2">
              Last updated: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunicationStats;